import type { ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import { SlotClient, SlotWorkflowClient } from '@frontend/slot/api';
import type { AvailableSlotConstraint, AvailableSlotListResponse, SlotQueryParams } from '@frontend/slot/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { syncExistingTransactionCollisAndPackagesLocation } from '../../utils/ensure-transaction-collis-and-packages';
import { syncOrderTransactionCollis } from '../../utils/sync-order-transaction-collis';
import { useScreen } from '../useScreen';

interface ViewProps {
    spotId: string | null;
    slotId: string | null;
    isFetchingSlots: boolean;
    availableSpotConstraints: AvailableSlotConstraint;
    availableSlotConstraints: AvailableSlotConstraint;
    setSpotId: (value: string | null) => void;
    setSlotId: (value: string | null) => void;
    fetchAvailableSlots: (
        accountId: string,
        spotId: string,
        constraints: AvailableSlotConstraint,
        queryParams?: ApiQueryParams<DefaultQueryParams & SlotQueryParams>
    ) => Promise<AvailableSlotListResponse>;
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (type: ScreenType) => void;
}

const useSelectLocationSlotRequired = (props: { wizard_id: string }): ViewProps => {
    const { store, onBack, updateWizardData, onSubmit: navigateToNextStep } = useScreen(props.wizard_id);
    const { syncTransaction } = useWizardTransaction({ wizardId: props.wizard_id, store });
    const [spotId, setSpotIdState] = useState<string | null>(store?.data.spotId || store?.data.transaction?.spot_id || null);
    const [slotId, setSlotIdState] = useState<string | null>(store?.data.slotId || store?.data.transaction?.slot_id || null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isFetchingSlots, setIsFetchingSlots] = useState<boolean>(false);
    const spotIdRef = useRef(spotId);
    const slotIdRef = useRef(slotId);
    const userClearedSlotRef = useRef(false);

    const availableSpotConstraints = useMemo<AvailableSlotConstraint>(
        () => ({
            account_id: store?.wizard.account_id,
            application: store?.functionality?.application,
            feature: store?.functionality?.feature,
            functionality_id: store?.functionality?.id,
            transaction_type: store?.data.transaction?.type ?? null,
            from_timestamp: store?.data.transaction?.schedule_start_timestamp,
            to_timestamp: store?.data.transaction?.schedule_stop_timestamp
        }),
        [
            store?.data.transaction?.type,
            store?.functionality?.application,
            store?.functionality?.feature,
            store?.functionality?.id,
            store?.wizard.account_id,
            store?.data.transaction?.schedule_start_timestamp,
            store?.data.transaction?.schedule_stop_timestamp
        ]
    );
    const availableSlotConstraints = useMemo<AvailableSlotConstraint>(
        () => ({
            ...availableSpotConstraints,
            spot_id: spotId ?? undefined
        }),
        [availableSpotConstraints, spotId]
    );

    useEffect(() => {
        spotIdRef.current = spotId;
    }, [spotId]);

    useEffect(() => {
        slotIdRef.current = slotId;
    }, [slotId]);

    useEffect(() => {
        if (store?.data.spotId) {
            setSpotIdState(store.data.spotId);
        } else if (store?.data.transaction?.spot_id) {
            setSpotIdState(store.data.transaction.spot_id);
        }

        if (store?.data.slotId) {
            setSlotIdState(store.data.slotId);
        } else if (store?.data.transaction?.slot_id) {
            setSlotIdState(store.data.transaction.slot_id);
        }
    }, [store?.data.slotId, store?.data.spotId, store?.data.transaction?.slot_id, store?.data.transaction?.spot_id]);

    const setSpotId = (value: string | null) => {
        const spotChanged = value !== spotIdRef.current;
        const nextSlotId = spotChanged ? null : slotIdRef.current;

        if (spotChanged) {
            userClearedSlotRef.current = false;
        }

        setSpotIdState(value);
        setSlotIdState(nextSlotId);

        if (store) {
            updateWizardData({
                spotId: value,
                slotId: nextSlotId,
                moduleId: nextSlotId ? store.data.moduleId : null
            });
        }
    };

    const setSlotId = async (value: string | null) => {
        setSlotIdState(value);

        if (!store) return;

        if (value) {
            const slot = (await SlotClient.resolveSlots([value])).results[0];
            updateWizardData({
                slotId: value,
                moduleId: slot?.module_id ?? null,
                spotId: slot?.spot_id ?? spotIdRef.current
            });
            if (slot?.spot_id && slot.spot_id !== spotIdRef.current) {
                setSpotIdState(slot.spot_id);
            }
            return;
        }

        updateWizardData({
            slotId: null,
            moduleId: null
        });
        userClearedSlotRef.current = true;
    };

    const fetchAvailableSlots = useCallback(
        async (
            accountId: string,
            selectedSpotId: string,
            constraints: AvailableSlotConstraint,
            queryParams?: ApiQueryParams<DefaultQueryParams & SlotQueryParams>
        ) => {
            setIsFetchingSlots(true);
            try {
                const result = await SlotWorkflowClient.postAvailableSlots(accountId, selectedSpotId, constraints, queryParams);

                if (!slotIdRef.current && !userClearedSlotRef.current && spotIdRef.current === selectedSpotId) {
                    const slot = result.results[0];
                    if (slot?.id) {
                        setSlotIdState(slot.id);
                        updateWizardData({
                            slotId: slot.id,
                            moduleId: slot.module_id ?? null,
                            spotId: slot.spot_id ?? selectedSpotId
                        });

                        if (slot.spot_id && slot.spot_id !== spotIdRef.current) {
                            setSpotIdState(slot.spot_id);
                        }
                    }
                }

                return result;
            } finally {
                setIsFetchingSlots(false);
            }
        },
        [updateWizardData]
    );

    const onSubmit = async () => {
        if (isSubmitting || !store?.data.transaction || !spotId || !slotId) return;

        setIsSubmitting(true);
        try {
            const slot = (await SlotClient.resolveSlots([slotId])).results[0];
            const transaction = await syncTransaction({
                spot_id: slot?.spot_id ?? spotId,
                slot_id: slotId
            });

            const location = {
                spot_id: slot?.spot_id ?? spotId,
                module_id: slot?.module_id ?? undefined,
                slot_id: slotId
            };

            await syncOrderTransactionCollis(store.data, transaction, {
                spotId: location.spot_id,
                moduleId: location.module_id ?? null,
                slotId: location.slot_id
            });

            const { collis, packages } = await syncExistingTransactionCollisAndPackagesLocation(store, transaction, location);

            updateWizardData({
                spotId: location.spot_id,
                slotId: location.slot_id,
                moduleId: location.module_id ?? null,
                collis,
                packages,
                transaction: {
                    ...transaction,
                    module_id: location.module_id ?? transaction.module_id
                },
                orderTransactionCollisCreated: true
            });

            const nextStep = store.steps.find((step) => step.screen === ScreenType.SELECT_LOCATION_SLOT_REQUIRED && step.trigger === ScreenTrigger.SUBMIT);
            navigateToNextStep(nextStep);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        spotId,
        slotId,
        isFetchingSlots,
        availableSpotConstraints,
        availableSlotConstraints,
        setSpotId,
        setSlotId,
        fetchAvailableSlots,
        submitDisabled: isSubmitting || !spotId || !slotId || !store?.data.transaction || isFetchingSlots,
        onSubmit,
        onBack
    };
};

export default useSelectLocationSlotRequired;
